import { useState, useEffect, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Tabs, Select, Slider } from "@/config/antd";
import { PlusOutlined, DownOutlined } from "@/config/antd";
import {
  fetchExpenseByAny,
  fetchExpenseTotals,
  selectFetchedExpenses,
  selectExpenseTotals,
} from "@/store/redux/slices/finance_management/expense_slice";
import {
  fetchFundByAny,
  selectFetchedFunds,
} from "@/store/redux/slices/finance_management/funds_slice";
import {
  fetchAccountByAny,
  selectFetchedAccounts,
} from "@/store/redux/slices/finance_management/account_slice";
import { useLoader } from "@/hooks/LoaderProvider";
import Button from "@/components/common/Button";
import Breadcrumb from "@/components/specific/Breadcrumb";
import Back from "@/components/specific/Back";
import Chart from "@/components/specific/Chart";
import TransactionCard from "@/pages/finance_manager/components/Transaction_Card";
import { mapExpenseTotals } from "@/utils/finance_utils";
import AddAccountDialog from "@/pages/finance_manager/components/Add_Account_Dialog";
import BankAccountsDialog from "@/pages/finance_manager/components/Bank_Account_Dialog";
import { getOrganizationId } from "@/utils/token_functions";
import accountCard from "@/assets/images/account_card.png";

// Selected account storage key
const SELECTED_ACCOUNT_KEY = "selectedAccountId";

const AccountManager = () => {
  const dispatch = useDispatch();
  const fetchedExpensesByOrg = useSelector(selectFetchedExpenses);
  const fetchedAccountsByOrg = useSelector(selectFetchedAccounts);
  const fetchedFundsByOrg = useSelector(selectFetchedFunds);
  const expenseTotals = useSelector(selectExpenseTotals);
  const [selectedTab, setSelectedTab] = useState("Income");
  const [showAddAccountDialog, setShowAddAccountDialog] = useState(false);
  const [showBankAccountsModal, setShowBankAccountsModal] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [chartData, setChartData] = useState([]);

  const funds = fetchedFundsByOrg || [];

  const monthlySummary = useMemo(() => {
    const mapped = mapExpenseTotals(expenseTotals);
    return {
      currentMonthFunds: mapped.currentMonthFunds,
      previousMonthFunds: mapped.previousMonthFunds,
      currentMonthExpenses: mapped.currentMonthExpenses,
      previousMonthExpenses: mapped.previousMonthExpenses,
      fundsPercentChange: mapped.fundsPercentChange,
      expensesPercentChange: mapped.expensesPercentChange,
    };
  }, [expenseTotals]);

  const { showLoader, hideLoader } = useLoader();

  const getAccountsToUse = useCallback(() => {
    return fetchedAccountsByOrg.length > 0
      ? fetchedAccountsByOrg
      : fetchedAccountsByOrg;
  }, [fetchedAccountsByOrg]);

  const getExpensesToUse = useCallback(() => {
    return fetchedExpensesByOrg.length > 0
      ? fetchedExpensesByOrg
      : fetchedExpensesByOrg;
  }, [fetchedExpensesByOrg]);

  useEffect(() => {
    const fetchData = async () => {
      showLoader();
      try {
        const organizationId = getOrganizationId();
        if (organizationId) {
          await Promise.all([
            dispatch(
              fetchExpenseByAny({
                key: "organizationID",
                value: organizationId,
              })
            ).unwrap(),
            dispatch(
              fetchFundByAny({
                key: "organizationID",
                value: organizationId,
              })
            ).unwrap(),
            dispatch(
              fetchAccountByAny({
                key: "organizationID",
                value: organizationId,
              })
            ).unwrap(),
            dispatch(fetchExpenseTotals(organizationId)).unwrap(),
          ]);
        }
      } catch (error) {
        console.error("Error fetching expenses and accounts:", error);
      } finally {
        hideLoader();
      }
    };

    fetchData();
  }, [dispatch, hideLoader, showLoader]);

  // Restore selected account from localStorage
  useEffect(() => {
    const accountsToUse = getAccountsToUse();

    // Restore selected account from localStorage if accounts are available
    if (accountsToUse.length > 0) {
      const savedAccountId = localStorage.getItem(SELECTED_ACCOUNT_KEY);
      if (savedAccountId) {
        const savedAccount = accountsToUse.find(
          (acc) => acc._id === savedAccountId
        );
        if (savedAccount) {
          setSelectedAccount(savedAccount);
        } else {
          // If selected account not found in current accounts, use the first one
          setSelectedAccount(accountsToUse[0]);
        }
      } else {
        // If no saved account, use the first one
        setSelectedAccount(accountsToUse[0]);
      }
    }
  }, [getAccountsToUse]);

  // Prepare chart data based on selected tab
  useEffect(() => {
    if (!getExpensesToUse().length && !funds.length) {
      setChartData([
        { time: "00:00", value: 0 },
        { time: "06:00", value: 0 },
        { time: "12:00", value: 0 },
        { time: "18:00", value: 0 },
        { time: "24:00", value: 0 },
      ]);
      return;
    }

    // Function to transform data for chart
    const prepareChartData = () => {
      const dataToUse = selectedTab === "Income" ? funds : getExpensesToUse();

      // Group by date and sort chronologically
      const sortedData = [...dataToUse].sort(
        (a, b) => new Date(a.createdAt) - new Date(b.createdAt)
      );

      // Get earliest and latest dates in the dataset
      const dates = sortedData.map((item) => new Date(item.createdAt));
      const earliestDate = dates.length
        ? new Date(Math.min(...dates))
        : new Date();
      const latestDate = dates.length
        ? new Date(Math.max(...dates))
        : new Date();

      // Calculate time range (in days)
      const timeRange = Math.max(
        Math.ceil((latestDate - earliestDate) / (1000 * 60 * 60 * 24)),
        1
      );

      // Group transactions by day
      const dailyTotals = {};

      sortedData.forEach((item) => {
        const date = new Date(item.createdAt);
        const dateStr = date.toISOString().split("T")[0]; // YYYY-MM-DD format

        if (!dailyTotals[dateStr]) {
          dailyTotals[dateStr] = 0;
        }

        // Add amount based on transaction type
        const amount =
          selectedTab === "Income"
            ? Number(item.amount || 0)
            : Number(item.grandTotal || item.amount || 0);

        dailyTotals[dateStr] += amount;
      });

      // Convert to array format expected by chart
      const chartPoints = [];

      // Create 5 points for the chart
      for (let i = 0; i <= 4; i++) {
        const pointDate = new Date(earliestDate);
        pointDate.setDate(
          earliestDate.getDate() + Math.floor((i * timeRange) / 4)
        );

        const dateStr = pointDate.toISOString().split("T")[0];
        const timeLabel = pointDate.toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
        });

        // Running total up to this point
        let runningTotal = 0;
        Object.keys(dailyTotals)
          .filter((date) => date <= dateStr)
          .forEach((date) => {
            runningTotal += dailyTotals[date];
          });

        chartPoints.push({
          time: timeLabel,
          value: runningTotal,
        });
      }

      return chartPoints;
    };

    setChartData(prepareChartData());
  }, [selectedTab, getExpensesToUse, funds]);

  // Transform expenses data for TransactionCard
  const transformedTransactions = getExpensesToUse().map((expense) => ({
    id: expense.id,
    _id: expense._id,
    title: expense.name,
    user: expense.merchantInformation?.name || "N/A",
    category: expense.itemCategoryID || "Uncategorized",
    amount: expense.amount?.toLocaleString() || "0",
    date: new Date(expense.createdAt).toLocaleDateString(),
    time: new Date(expense.createdAt).toLocaleTimeString(),
  }));

  const handleOpenAddAccountDialog = () => {
    showLoader();
    setTimeout(() => {
      hideLoader();
      setShowAddAccountDialog(true);
    }, 2000);
  };

  const handleOpenBankAccountDialog = () => {
    setShowBankAccountsModal(true);
  };

  const handleAccountSelect = (account) => {
    setSelectedAccount(account);
    // Save selected account ID to localStorage
    localStorage.setItem(SELECTED_ACCOUNT_KEY, account._id);
    setShowBankAccountsModal(false);
  };

  const formatCurrency = (value) => {
    return `₦${value.toLocaleString()}`;
  };

  // Calculate slider marks based on selected account
  const sliderMarks = useMemo(() => {
    if (!selectedAccount) return { 0: "₦0", 70: "₦70K" };

    const spendingLimit = selectedAccount.spendingLimit;
    return {
      0: "₦0",
      70: formatCurrency(spendingLimit),
    };
  }, [selectedAccount]);

  // Calculate current value for slider
  const currentSliderValue = useMemo(() => {
    if (!selectedAccount) return 30; // Default value as in original UI

    // Calculate percentage relative to max value of 70
    return Math.min(
      (selectedAccount.amountToDeposit / selectedAccount.spendingLimit) * 70,
      70
    );
  }, [selectedAccount]);

  // breadcrumb items
  const breadCrumbItems = [
    { title: "Financial Management", link: "/dashboard/financial" },
    { title: "Accounts", link: null },
  ];

  return (
    <div className="w-full py-6 text-left lg:px-5 sm:py-0">
      <Breadcrumb items={breadCrumbItems} />
      <Back text="Back" />
      <div className="grid-cols-3 gap-10 mt-6 lg:grid">
        {/* Summary Section */}
        <div className="lg:col-span-2 lg:border-2 border-secondary rounded-xl p-5">
          <h1 className="mb-4 text-2xl font-bold">Summary</h1>
          <div className="flex gap-5 pb-4 overflow-x-auto hide-vertical-scrollbar">
            <div className="min-w-[274px] lg:w-full bg-secondary p-5 rounded-[10px] border border-secondary flex items-center justify-between">
              <div>
                <h2 className="mb-2 text-lg font-bold">TOTAL FUNDS</h2>
                <div className="flex justify-between gap-5">
                  <div>
                    <p className="text-xs text-lighterGrey">This Month</p>
                    <p className="text-xl font-bold">
                      {formatCurrency(monthlySummary.currentMonthFunds)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-lighterGrey">Last Month</p>
                    <p className="text-xl font-bold">
                      {formatCurrency(monthlySummary.previousMonthFunds)}
                    </p>
                  </div>
                </div>
              </div>
              <div className="relative flex-shrink-0 hidden lg:block w-14 h-14 sm:w-16 sm:h-16">
                <svg className="w-full h-full transform -rotate-90">
                  <circle
                    cx="50%"
                    cy="50%"
                    r="45%"
                    stroke="#E5E7EB"
                    strokeWidth="4"
                    fill="none"
                  />
                  <circle
                    cx="50%"
                    cy="50%"
                    r="45%"
                    stroke={
                      monthlySummary.fundsPercentChange >= 0
                        ? "var(--veegil-color-primary)"
                        : "var(--veegil-color-danger)"
                    }
                    strokeWidth="4"
                    fill="none"
                    strokeDasharray={175.93}
                    strokeDashoffset={
                      175.93 *
                      (1 - Math.abs(monthlySummary.fundsPercentChange) / 100)
                    }
                  />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span
                    className={`text-base font-semibold sm:text-lg ${
                      monthlySummary.fundsPercentChange >= 0
                        ? "text-primary"
                        : "text-danger"
                    }`}>
                    {Math.abs(monthlySummary.fundsPercentChange)}%
                  </span>
                </div>
              </div>
            </div>

            <div className="min-w-[274px] lg:w-full bg-secondary p-5 rounded-[10px] border border-secondary flex items-center justify-between">
              <div>
                <h2 className="mb-2 text-lg font-bold">TOTAL EXPENSES</h2>
                <div className="flex justify-between gap-5">
                  <div>
                    <p className="text-xs text-lighterGrey">This Month</p>
                    <p className="text-xl font-bold">
                      {formatCurrency(monthlySummary.currentMonthExpenses)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-lighterGrey">Last Month</p>
                    <p className="text-xl font-bold">
                      {formatCurrency(monthlySummary.previousMonthExpenses)}
                    </p>
                  </div>
                </div>
              </div>
              <div className="relative flex-shrink-0 hidden lg:block w-14 h-14 sm:w-16 sm:h-16">
                <svg className="w-full h-full transform -rotate-90">
                  <circle
                    cx="50%"
                    cy="50%"
                    r="45%"
                    stroke="#E5E7EB"
                    strokeWidth="4"
                    fill="none"
                  />
                  <circle
                    cx="50%"
                    cy="50%"
                    r="45%"
                    stroke={
                      monthlySummary.expensesPercentChange >= 0
                        ? "var(--veegil-color-primary)"
                        : "var(--veegil-color-danger)"
                    }
                    strokeWidth="4"
                    fill="none"
                    strokeDasharray={175.93}
                    strokeDashoffset={
                      175.93 *
                      (1 - Math.abs(monthlySummary.expensesPercentChange) / 100)
                    }
                  />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span
                    className={`text-base font-semibold sm:text-lg ${
                      monthlySummary.expensesPercentChange >= 0
                        ? "text-primary"
                        : "text-danger"
                    }`}>
                    {Math.abs(monthlySummary.expensesPercentChange)}%
                  </span>
                </div>
              </div>
            </div>
          </div>
          {/* Transactions List */}
          <div className="hidden mt-10 lg:block">
            {transformedTransactions.map((transaction, index) => (
              <TransactionCard key={transaction.id || index} {...transaction} />
            ))}
          </div>
        </div>

        <div className="lg:border-2 border-[#eee] rounded-xl p-5">
          {/* Account Section */}
          <div className="mt-10 lg:mt-0">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-bold heading-medium">Account</h2>
              <Button
                text="ADD NEW"
                icon={<PlusOutlined />}
                iconPosition="left"
                className="border border-primary text-small whitespace-nowrap rounded-[5px] w-[121px] h-[36px]"
                customBgColor="white"
                customTextColor="primary"
                onClick={handleOpenAddAccountDialog}
              />

              <AddAccountDialog
                isVisible={showAddAccountDialog}
                onClose={() => setShowAddAccountDialog(false)}
              />
            </div>

            <div className="bg-white p-5 rounded-[10px] border border-[#E5E7EB]">
              <div className="flex items-start justify-between mb-6">
                <p className="font-medium uppercase text-small-xs text-lighterGrey">
                  CURRENT BALANCE
                </p>
                <Button
                  text="Change account"
                  icon={<DownOutlined />}
                  iconPosition="right"
                  className="border border-primary whitespace-nowrap text-small-xs font-normal rounded-[5px] w-[135px] h-[38px] flex items-center justify-center gap-2"
                  customBgColor="white"
                  customTextColor="black"
                  onClick={handleOpenBankAccountDialog}
                />

                <BankAccountsDialog
                  isVisible={showBankAccountsModal}
                  onClose={() => setShowBankAccountsModal(false)}
                  accounts={getAccountsToUse()}
                  onSelectAccount={handleAccountSelect}
                  onAddNewClick={handleOpenAddAccountDialog}
                />
              </div>

              <div>
                <h3 className="text-[2.5rem] font-bold text-primary mb-2">
                  ₦
                  {selectedAccount
                    ? selectedAccount.amountToDeposit.toLocaleString()
                    : "0"}
                </h3>
                <div className="flex items-center justify-between">
                  <p className="text-small-xs text-lighterGrey">
                    {selectedAccount
                      ? `${selectedAccount.bankName} Account`
                      : "Current Account"}
                  </p>
                  <img
                    src={accountCard}
                    alt="Wise"
                    className="object-contain h-6 w-[50px]"
                  />
                </div>
              </div>
            </div>

            {/* Monthly Expense Limit */}
            <div className="mt-6">
              <p className="mb-2 text-small-xs text-lighterGrey">
                Monthly Expense Limit
              </p>
              <Slider
                value={currentSliderValue}
                min={0}
                max={70}
                marks={sliderMarks}
              />
            </div>
          </div>

          {/* Statistics Section */}
          <div className="mt-10">
            <div className="flex justify-between mb-4">
              <h2 className="font-bold heading-medium">Statistics</h2>
              <Select
                labelInValue
                defaultValue={{
                  value: "today",
                  label: "Today",
                }}
                style={{
                  width: "8.0625rem",
                  height: "2.375rem",
                  borderRadius: "5px",
                  border: "1px solid #0066FF",
                }}
                options={[
                  { value: "today", label: "Today" },
                  { value: "week", label: "This Week" },
                  { value: "month", label: "This Month" },
                ]}
              />
            </div>
            <Tabs
              defaultActiveKey="Income"
              items={[
                { label: "Income", key: "Income" },
                { label: "Expense", key: "Expense" },
              ]}
              onChange={(key) => setSelectedTab(key)}
              className="custom-tabs-2"
              type="card"
              tabBarStyle={{
                border: "none",
                margin: 0,
              }}
            />

            <div className="mt-4">
              <Chart data={chartData} type={selectedTab} />
            </div>
          </div>
        </div>
      </div>

      {/* Transactions List */}
      <div className="mt-10 lg:hidden">
        {transformedTransactions.map((transaction, index) => (
          <TransactionCard key={transaction.id || index} {...transaction} />
        ))}
      </div>
    </div>
  );
};

export default AccountManager;
